/**
 * Payment Status Utilities
 * Utilities for calculating and formatting payment status based on financial data
 */

export type PaymentStatus = 'no_transactions' | 'paid' | 'partial' | 'overdue';

/**
 * Calculate payment status based on financial data
 * @param totalDue - Total amount due
 * @param totalPaid - Total amount paid
 * @returns Payment status string
 */
export function calculatePaymentStatus (
  totalDue: string | number | undefined | null,
  totalPaid: string | number | undefined | null
): PaymentStatus {
  const due = totalDue ? parseFloat(String(totalDue)) : 0;
  const paid = totalPaid ? parseFloat(String(totalPaid)) : 0;

  // If no transactions (both due and paid are 0)
  if (due === 0 && paid === 0) {
    return 'no_transactions';
  }

  // If fully paid (paid >= due)
  if (paid >= due) {
    return 'paid';
  }

  // If partially paid (paid > 0 but less than due)
  if (paid > 0) {
    return 'partial';
  }

  // If overdue (due > 0 and paid = 0)
  return 'overdue';
}

/**
 * Get payment status configuration for UI display
 * @param status - Payment status
 * @returns Status configuration object
 */
export function getPaymentStatusConfig (status: PaymentStatus | string | undefined | null) {
  if (!status) {
    return {
      statusConfig: {
        className: 'bg-gray-50 text-gray-600 border border-gray-200'
      },
      statusText: 'غير محدد',
      tooltip: 'حالة غير محددة'
    };
  }

  switch (status.toLowerCase()) {
    case 'no_transactions':
      return {
        statusConfig: {
          className: 'bg-gray-100 text-gray-700 border border-gray-200'
        },
        statusText: 'جديد',
        tooltip: 'متاجر جديدة لا يوجد معاملات'
      };
    case 'overdue':
      return {
        statusConfig: {
          className: 'bg-red-50 text-red-700 border border-red-200'
        },
        statusText: 'متأخر',
        tooltip: 'تحتاج تحصيل فوري'
      };
    case 'partial':
      return {
        statusConfig: {
          className: 'bg-orange-50 text-orange-700 border border-orange-200'
        },
        statusText: 'جزئي',
        tooltip: 'تحتاج متابعة المدفوعات المتبقية'
      };
    case 'paid':
      return {
        statusConfig: {
          className: 'bg-green-50 text-green-700 border border-green-200'
        },
        statusText: 'مدفوع',
        tooltip: 'جيدة - يمكن تشجيعها للمزيد من المبيعات'
      };
    default:
      return {
        statusConfig: {
          className: 'bg-gray-50 text-gray-600 border border-gray-200'
        },
        statusText: 'غير محدد',
        tooltip: 'حالة غير محددة'
      };
  }
}
